import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromPath, type HeadPicture, inEllipse, mask, type SetFlair, toneOf } from './mythic';

/**
 * The Inferno set (ADR-0160, mythic): a burning skull, cinder mail under a cape on fire, and the Hellfire gun.
 *
 * The head is a skull in a crown of fire (a mask over the whole head, tied on): bone lit from the top-left, two deep
 * sockets under angry brows with an eye of the team's light burning in each, a nose's hole, a row of teeth; tongues of
 * flame stand behind its crown in the picture, and its flair burns on them: flames rising off the crown and blown back
 * on the run, embers drifting up, a glow breathing behind it. The mail is blackened plate with a spiked pauldron on each
 * shoulder, cracked over magma, the team's belt at the waist; its cape (flair: cloth on a chain of links) hangs dark from
 * the shoulders and burns at its torn hem, streams out behind on the run and leaves burning footprints.
 *
 * Rubric (`assetc mythic` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead; the admin app's Skins
 * page, both previews; the `mythic-inferno` playtest: the Store, PLAY's stage, a round at 1920×1080): skull S5 R5 P4 L4
 * C3 A4 F4, mail S4 R4 P4 L4 C4 F4. Still weak: the crown's lit patch and the temple's shade step at 5×; the mail shows
 * little past the gun (its pauldron and the cape carry it); the skull's teeth are 3 px each, a white band at 1:1.
 */

// --- The burning skull -----------------------------------------------------------------------------------------------

/** Bone by the light on it: a small sheen, cream, tan in the half-light, brown turned away. */
const BONE = [
  ['w', 0.975],
  ['e', 0.5],
  ['t', 0.12],
  ['m', -0.25],
] as const;
/** From the head's middle (u forward, v down): the cranium, the jaw under it (set forward: the skull looks right). */
const CRANIUM: Ellipse = [0, -2, 21, 14.5];
const JAW: Ellipse = [3.5, 7, 15, 11.5];
/** The sockets, near and far (each brow slants down to the nose), and the brow's ridge over them. */
const NEAR: Ellipse = [-4.5, 3.4, 6.2, 6.3];
const FAR: Ellipse = [11.5, 3.4, 4.6, 5.9];
const RIDGE = 2.4;
/** A crack down the crown. */
const CRACK = [12, -15, 9, -11, 11, -7, 9, -4];

/**
 * Low tongues of flame behind the crown, for where nothing moves (a portrait, a tile): the fire itself is the head's flair,
 * standing tall over these. [foot u, foot v, height, half-width at the foot, lean].
 */
const TONGUES: readonly (readonly [number, number, number, number, number])[] = [
  [-21, 6, 10, 4.5, -0.5],
  [-18, -5, 8, 5, -0.3],
  [-10, -12, 7, 5.5, -0.08],
  [0, -15, 7, 6, 0.06],
  [10, -13, 7, 5.5, 0.16],
  [18, -6, 8, 4.5, 0.3],
];

/** The fire at (u, v), or null outside every tongue: white at a tongue's heart, yellow, gold, orange, rust at its tip. */
function fire(u: number, v: number): PaletteKey | null {
  let best: PaletteKey | null = null;
  let rank = 9;
  for (const [fu, fv, h, w, lean] of TONGUES) {
    const t = (fv - v) / h;
    if (t < -0.35 || t > 1) continue;
    const up = Math.max(0, t);
    const half = w * (1 - up) ** 0.75 * (t < 0 ? 1 + t : 1);
    const mid = fu + lean * (fv - v) + Math.sin(up * 5 + fu) * 1.3 * up;
    const d = Math.abs(u - mid) / Math.max(0.01, half);
    if (d >= 1) continue;
    const r = d < 0.3 && up < 0.3 ? 0 : d < 0.5 && up < 0.55 ? 1 : d < 0.75 && up < 0.8 ? 2 : up > 0.85 ? 4 : 3;
    if (r < rank) [rank, best] = [r, (['w', 'y', 'Y', 'o', 'u'] as const)[r] as PaletteKey];
  }
  return best;
}

const skull: HeadPicture = (u, v, lx, ly) => {
  const inCranium = inEllipse(u, v, CRANIUM) <= 1;
  const inJaw = inEllipse(u, v, JAW) <= 1 && v > 0;
  if (!inCranium && !inJaw) return fire(u, v);
  // The sockets under their brows: dark, an eye of the team's light low in each.
  let ridge = false;
  for (const [e, slant] of [
    [NEAR, 0.4],
    [FAR, -0.4],
  ] as const) {
    const brow = e[1] - e[3] + 2 + (u - e[0]) * slant;
    const r = inEllipse(u, v, e);
    if (r <= 1 && v >= brow) {
      const eye = Math.hypot(u - e[0], (v - (e[1] + 1.4)) * 0.9);
      if (eye < 1.25) return 'w';
      if (eye < 2.3) return 'c';
      if (eye < 3.3) return 'b';
      return v < brow + 1.6 ? 'N' : 'k';
    }
    // The brow's ridge: the bone standing proud over the socket, catching the light.
    if (v < brow && v > brow - RIDGE && Math.abs(u - e[0]) < e[2] + 1.5) ridge = true;
  }
  // The nose's hole: a narrow heart, point up.
  const nose = (v - 9.4) * 0.62;
  if (v >= 9.4 && v <= 13.4 && Math.abs(u - 7.2) < nose) return Math.abs(u - 7.2) < nose - 1 ? 'k' : 'N';
  // The teeth under the maxilla's shadow, a gap every fourth px.
  if (u > -5.5 && u < 17 && inEllipse(u, v + 1.5, JAW) <= 1) {
    if (v >= 14.4 && v < 15.3) return 'n';
    if (v >= 15.3 && v < 18) return ((Math.floor(u + 5.5) % 4) + 4) % 4 === 3 ? 'm' : v < 16.3 ? 'w' : 'e';
  }
  if (fromPath(u, v, CRACK) < 0.7) return 'm';
  const l = inCranium ? domeLight(u, v, CRANIUM, lx, ly) : domeLight(u, v, JAW, lx, ly) - 0.25;
  // The cheek's hollow under the near socket, the temple's behind it.
  const hollow = inEllipse(u, v, [-10, 11, 5, 3.5]) <= 1 || inEllipse(u, v, [-16.5, 2.5, 2.6, 5]) <= 1;
  return toneOf(l + (ridge ? 0.3 : 0) - (hollow ? 0.4 : 0), BONE, 'n');
};

// --- Cinder mail -----------------------------------------------------------------------------------------------------

/** A crack with magma in it on `c`: orange along `pts` (frame px, x, y pairs), gold where it widens. */
function magma(c: PixelCanvas, x0: number, y0: number, pts: readonly number[]): void {
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const [ax, ay, bx, by] = [pts[i], pts[i + 1], pts[i + 2], pts[i + 3]] as [number, number, number, number];
    const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
    for (let k = 0; k <= n; k++) {
      const [x, y] = [Math.round(ax + ((bx - ax) * k) / n) - x0, Math.round(ay + ((by - ay) * k) / n) - y0];
      if (c.get(x, y) !== null) c.set(x, y, (k + i) % 3 === 1 ? 'Y' : 'o');
    }
    c.tint(ax - x0, ay - y0, 'y');
  }
}

/**
 * The mail standing (the frame's x 6-55, y 39-55): a breastplate of blackened steel, lit along its top-left edges, a
 * gorget in the chin's shadow, a pauldron on each shoulder with a spike up, cracks of magma over the lot, the team's
 * belt (a gold buckle) at the waist.
 */
function mail(): Part {
  const [x0, y0] = [6, 39];
  const c = new PixelCanvas(50, 17);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 44; y <= 52; y++)
    for (let x = 16; x <= 43; x++) {
      const edge = x === 16 || y === 44;
      at(x, y, edge ? '3' : x <= 18 || y === 45 ? '2' : x >= 40 || y >= 51 ? 'k' : '1');
    }
  // The gorget under the chin.
  for (let x = 22; x <= 38; x++) {
    at(x, 43, x < 28 ? '3' : '2');
    at(x, 44, 'k');
  }
  for (const [cx, side] of [
    [12.5, -1],
    [47, 1],
  ] as const) {
    const e: Ellipse = [cx, 47.5, 7, 5];
    for (let y = 41; y <= 53; y++)
      for (let x = 5; x <= 55; x++) {
        const r = inEllipse(x + 0.5, y + 0.5, e);
        if (r > 1) continue;
        const l = -(x + 0.5 - cx) / 7 - (y + 0.5 - 47.5) / 5;
        at(x, y, r > 0.72 && l > 0.2 ? '4' : l > 0.5 ? '3' : l > -0.4 ? '2' : '1');
      }
    // Its spike, leaning out.
    for (let k = 0; k < 5; k++) {
      const x = Math.round(cx + side * (1 + k * 0.7));
      for (let w = 0; w < 4 - Math.floor(k * 0.8); w++)
        at(x + (side < 0 ? -w : w) - (side < 0 ? 0 : 1), 43 - k, w === 0 ? '4' : '2');
    }
  }
  magma(c, x0, y0, [18, 47, 22, 49, 26, 47, 31, 50, 36, 48, 41, 51]);
  magma(c, x0, y0, [9, 46, 12, 48, 15, 46]);
  magma(c, x0, y0, [44, 47, 47, 49, 50, 47]);
  // The magma's heart on the breast.
  c.stamp(
    `
    .oo.
    oywo
    oyyo
    .oo.
  `,
    21 - x0,
    46 - y0,
  );
  for (let x = 16; x <= 43; x++) {
    at(x, 53, 'c');
    at(x, 54, 'b');
    at(x, 55, 'B');
  }
  c.stamp(
    `
    yYY
    YyU
    YUU
  `,
    29 - x0,
    53 - y0,
  );
  return [c, x0, y0];
}

/** The mail lying (right of the legs, under the head): the belt at the waist (left), the breastplate cracked over magma. */
const DEAD_MAIL = grid(`
  ....2233
  ...22332
  c2221112
  b2111o11
  b211oY11
  B21o1111
  b21o1111
  b211o111
  b2111oY1
  B211yyo1
  b211yo11
  b21o1111
  b2o11111
  B1Y11111
  b1o1111.
  b11o1111
  b111kkkk
  ....kkkk
`);

/** Blackened greaves and boots under the team's trousers. */
const CINDER_MAIL = (): Outfit => ({ torso: mail(), dead: [DEAD_MAIL, 24, 51], legs: { n: '3', N: '1' } });

// --- Hellfire on every other gun -------------------------------------------------------------------------------------

/**
 * The Hellfire finish (the default gun is its own, `pollinator_inferno`): obsidian where the steel was, its glints dull,
 * embers where the wood was (deep red to orange) and molten brass.
 */
const HELLFIRE = {
  '1': 'k',
  '2': 'k',
  '3': '1',
  '4': '2',
  '5': '3',
  N: 'k',
  n: 'N',
  m: 'R',
  t: 'u',
  e: 'o',
  U: 'u',
  Y: 'o',
  y: 'Y',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

/** A flame outside in (its edge, its body, its heart), and what flies off one. */
const BLAZE = [0xf77622, 0xfeae34, 0xfee761] as const;
const HOT = [0xfeae34, 0xfee761, 0xffffff] as const;
const SPARKS = [0xfee761, 0xfeae34, 0xf77622, 0xbe4a2f] as const;
const EMBER = [0xfee761, 0xf77622, 0xe43b44] as const;
/** A flake of flame off a tongue's tip. */
const FLAKE = {
  rate: 3.5,
  v: [-6, 6, -44, -24],
  carry: 0.5,
  g: -30,
  life: [0.2, 0.4],
  size: [3, 1],
  tones: SPARKS,
} as const;

const FLAIR: SetFlair = {
  head: [
    // The fire's light behind the skull, breathing.
    {
      kind: 'pulse',
      at: { on: 'head', x: 30, y: 18 },
      r: [20, 25],
      alpha: [0.18, 0.32],
      period: 0.85,
      tone: 0xf77622,
      layer: 'back',
    },
    // The crown of fire: tongues standing on the skull's crown, behind it, blown back on the run.
    {
      kind: 'flame',
      at: { on: 'head', x: 10, y: 22 },
      h: 15,
      w: 5,
      tones: BLAZE,
      speed: 2.2,
      blown: 0.035,
      shed: FLAKE,
      layer: 'back',
    },
    {
      kind: 'flame',
      at: { on: 'head', x: 19, y: 15 },
      h: 21,
      w: 6,
      tones: BLAZE,
      speed: 2,
      blown: 0.035,
      shed: FLAKE,
      layer: 'back',
    },
    {
      kind: 'flame',
      at: { on: 'head', x: 30, y: 13 },
      h: 27,
      w: 7,
      tones: HOT,
      speed: 1.8,
      blown: 0.035,
      shed: FLAKE,
      layer: 'back',
    },
    {
      kind: 'flame',
      at: { on: 'head', x: 41, y: 15 },
      h: 21,
      w: 6,
      tones: BLAZE,
      speed: 2.1,
      blown: 0.035,
      shed: FLAKE,
      layer: 'back',
    },
    {
      kind: 'flame',
      at: { on: 'head', x: 50, y: 22 },
      h: 14,
      w: 5,
      tones: BLAZE,
      speed: 2.4,
      blown: 0.035,
      shed: FLAKE,
      layer: 'back',
    },
    // Licks of it over the brow, in front of the bone.
    {
      kind: 'flame',
      at: { on: 'head', x: 23, y: 15 },
      h: 10,
      w: 3,
      tones: BLAZE,
      speed: 2.6,
      blown: 0.03,
      layer: 'front',
    },
    {
      kind: 'flame',
      at: { on: 'head', x: 37, y: 14 },
      h: 11,
      w: 3,
      tones: HOT,
      speed: 2.3,
      blown: 0.03,
      layer: 'front',
    },
    // Its eyes burn in the team's light.
    {
      kind: 'pulse',
      at: { on: 'head', x: 25.5, y: 33 },
      r: [4, 6.5],
      alpha: [0.3, 0.6],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'head', x: 41.5, y: 33 },
      r: [3.5, 5.5],
      alpha: [0.3, 0.6],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    // Embers drifting up out of it.
    {
      kind: 'emit',
      at: { on: 'head', x: 30, y: 6 },
      box: [34, 10],
      rate: 6,
      v: [-9, 9, -34, -16],
      carry: 0.5,
      g: -10,
      drag: 0.5,
      wobble: [2, 1.4],
      life: [0.9, 1.6],
      size: [1, 1],
      tones: EMBER,
      add: true,
      glow: 7,
      layer: 'front',
    },
  ],
  outfit: [
    // The cape: dark cloth from the shoulders, burning at its torn hem.
    {
      kind: 'chain',
      at: { on: 'torso', x: 13, y: 44 },
      n: 11,
      seg: 2.2,
      dir: Math.PI * 0.58,
      stiff: 5,
      g: 320,
      drag: 0.04,
      wind: 420,
      w: [14, 20, 26],
      shape: 'bars',
      tones: [0x3a4466, 0x262b44, 0x262b44, 0x181425, 0x181425, 0x3e2731, 0xa22633, 0xf77622, 0xfeae34],
      rim: 0x181425,
      tatter: 5,
      shed: { rate: 26, box: [20, 2], v: [-6, 6, -40, -16], g: -30, life: [0.2, 0.45], size: [4, 1], tones: SPARKS },
      layer: 'back',
    },
    // Embers off the mail's cracks.
    {
      kind: 'emit',
      at: { on: 'torso', x: 26, y: 48 },
      box: [26, 8],
      rate: 3,
      v: [-8, 8, -30, -12],
      carry: 0.5,
      g: -8,
      drag: 0.5,
      wobble: [2, 1.2],
      life: [0.8, 1.4],
      size: [1, 1],
      tones: EMBER,
      add: true,
      glow: 6,
      layer: 'front',
    },
    // Burning footprints on the run.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 69 },
      box: [12, 2],
      rate: 0,
      run: 0.085,
      v: [-2, 2, -16, -6],
      g: -14,
      life: [0.35, 0.75],
      size: [4, 1],
      tones: SPARKS,
      layer: 'back',
    },
  ],
  gun: [
    // A pilot flame at the muzzle, the furnace's light on the gun and its embers.
    { kind: 'flame', at: { on: 'gun', x: 62, y: 19 }, h: 8, w: 2.4, tones: HOT, speed: 3, blown: 0.04, layer: 'gun' },
    {
      kind: 'pulse',
      at: { on: 'gun', x: 34, y: 12 },
      r: [6, 9],
      alpha: [0.2, 0.42],
      period: 0.6,
      tone: 0xf77622,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 34, y: 8 },
      box: [10, 3],
      rate: 4,
      v: [-6, 6, -26, -12],
      g: -8,
      drag: 0.5,
      life: [0.6, 1.1],
      size: [1, 1],
      tones: EMBER,
      add: true,
      glow: 6,
      layer: 'gun',
    },
  ],
};

/** The Inferno set (ADR-0160). */
export const INFERNO = drawnLater({ head: () => mask(skull), outfit: CINDER_MAIL, gun: HELLFIRE, flair: FLAIR });
